import type { EditorActions } from '@atlaskit/editor-core';

import type { ButtonStyle, CommonLayout, Height } from '../../types';

export type UseExtensionParametersAndActionsProps = {
	localId: string;
	editorActions: EditorActions;
};

export type UpdateField<TParameters> = Partial<TParameters>;
export type UpdateFormFieldForAllItemsType<TItemParameters> = (
	updatedField: UpdateField<TItemParameters>,
) => void;
export type UpdateMainFormFieldType<TExtensionParameters> = (
	updatedField: UpdateField<TExtensionParameters>,
	options?: { fieldsToRemove?: string[] },
) => void;

export type UpdateItemFormFieldType = (itemId: string, updatedField: UpdateField<Item>) => void;
export type AddItemType = () => void;
export type RemoveItemType = (itemId: string) => void;

export enum ITEM_FIELDS {
	CARD_ID = 'cardId',
	TITLE = 'title',
	IS_TITLE_USER_EDITED = 'isTitleUserEdited',
	DESCRIPTION = 'description',
	IS_DESCRIPTION_USER_EDITED = 'isDescriptionUserEdited',
	LABEL = 'label',
	IS_LABEL_HIDDEN = 'isLabelHidden',
	LINK = 'link',
	IS_LINK_USER_EDITED = 'isLinkUserEdited',
	IMAGE_SRC = 'imageSrc',
	CONFLUENCE_PAGE_ID = 'confluencePageId',
	IMAGE_ALT_TEXT = 'imageAltText',
	SEGMENTATION_TARGETS = 'segmentationTarget',
	AUDIENCE_SEGMENTATION_GROUP_IDS = 'audienceSegmentationGroupIds', // group preferences stored via this field when segmentationTarget is EACH
	LINK_RESOURCE_TYPE = 'linkResourceType',
	BACKGROUND_COLOR = 'backgroundColor',
	TEXT_COLOR = 'textColor',
	LABEL_COLOR = 'labelColor',
	LAYOUT = 'layout',
	IS_BUTTON_SHOWN = 'isButtonShown',
	BUTTON_TEXT = 'buttonText',
	BUTTON_STYLE = 'buttonStyle',
	HEIGHT = 'height',
}

export type Item = {
	[ITEM_FIELDS.CARD_ID]: string;
	[ITEM_FIELDS.LINK]: string;
	[ITEM_FIELDS.IS_LINK_USER_EDITED]?: boolean;
	[ITEM_FIELDS.TITLE]?: string;
	[ITEM_FIELDS.IS_TITLE_USER_EDITED]?: boolean;
	[ITEM_FIELDS.DESCRIPTION]?: string;
	[ITEM_FIELDS.IS_DESCRIPTION_USER_EDITED]?: boolean;
	[ITEM_FIELDS.LABEL]?: string;
	[ITEM_FIELDS.IS_LABEL_HIDDEN]?: boolean;
	[ITEM_FIELDS.IMAGE_SRC]?: string;
	[ITEM_FIELDS.IMAGE_ALT_TEXT]?: string;
	[ITEM_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]?: string[];
	[ITEM_FIELDS.LINK_RESOURCE_TYPE]?: string;
	[ITEM_FIELDS.CONFLUENCE_PAGE_ID]?: string;
	[ITEM_FIELDS.BACKGROUND_COLOR]?: string;
	[ITEM_FIELDS.TEXT_COLOR]?: string;
	[ITEM_FIELDS.LABEL_COLOR]?: string;
	[ITEM_FIELDS.LAYOUT]?: CommonLayout;
	[ITEM_FIELDS.IS_BUTTON_SHOWN]?: boolean;
	[ITEM_FIELDS.BUTTON_TEXT]?: string;
	[ITEM_FIELDS.BUTTON_STYLE]?: ButtonStyle;
	[ITEM_FIELDS.HEIGHT]?: Height;
	[ITEM_FIELDS.SEGMENTATION_TARGETS]?: SegmentationTargets;
};

export enum CONDITIONAL_FIELDS {
	IS_AVATAR_SHOWN = 'isAvatarShown',
	IS_PUBLISH_DATE_SHOWN = 'isPublishDateShown',
	IS_LABEL_SHOWN = 'isLabelShown',
	IS_SEGMENTATION_SHOWN = 'isSegmentationShown',
	IS_DESCRIPTION_SHOWN = 'isDescriptionShown',
}

export type ConditionalFields = {
	[CONDITIONAL_FIELDS.IS_AVATAR_SHOWN]?: boolean;
	[CONDITIONAL_FIELDS.IS_PUBLISH_DATE_SHOWN]?: boolean;
	[CONDITIONAL_FIELDS.IS_SEGMENTATION_SHOWN]?: boolean;
	[CONDITIONAL_FIELDS.IS_LABEL_SHOWN]?: boolean;
	[CONDITIONAL_FIELDS.IS_DESCRIPTION_SHOWN]?: boolean;
};

export enum RenderVariant {
	MANUAL = 'manual',
	DYNAMIC = 'dynamic',
}

export enum SegmentationTargets {
	ALL = 'all',
	EACH = 'each',
}

export type ItemDimensions = {
	minWidth: number;
	maxWidth: number;
	height: number;
};
